//jun2 homecard without refactor of menu:
import React, {
  useState,
  memo,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  ButtonBase,
  Dialog,
  Grid,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for routing
import ShareIcon from '@mui/icons-material/Share';
import ShareForm from '../Share/ShareForm'; // Import ShareForm
import { useStorageUrl } from '../../hooks/useStorageUrl';   // ← adjust if your relative path differs

import { useAuth } from '../../contexts/AuthContext'; 
import HomeFormActionsMenu from './HomeFormActionsMenu';

// Custom color palette for UTM
const utmColors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58'];

// Match the cleaner Template Gallery FormCard visual dimensions.
const HOME_FORM_CARD_WIDTH = 200;
const HOME_FORM_CARD_IMAGE_HEIGHT = 150;

/** -------------------------------------------------------------------------
 *  Screenshot Storage URL policy
 *
 *  ENTERPRISE CONTEXT
 *  - Home cards render form screenshots produced by the screenshot service.
 *  - useStorageUrl is intentionally conservative and no longer treats all
 *    assets/... paths as public, because private DAM assets can share the
 *    same derivative path patterns.
 *  - HomeCard knows these paths are form screenshots, so it must pass the
 *    screenshot policy explicitly.
 *
 *  This preserves the hardened DAM model:
 *  - screenshots/icons/banners may use public-form-image GCS URLs
 *  - private DAM assets must use signed/backend URLs
 *  ----------------------------------------------------------------------*/
const SCREENSHOT_STORAGE_URL_POLICY = Object.freeze({
  assetType: 'screenshot',
  preferPublicUrl: true,
  allowPublicFallback: true,
});

const HomeCard = memo(({ form, onDelete }) => {
  const [showShareForm, setShowShareForm] = useState(false); // Local state for showing ShareForm
  const navigate = useNavigate(); // Hook to navigate between pages

const formId = form.id; // Access formId from the form object

/* ───────────────────────────────────────────────────────────────
   SAFETY: prevent screenshot “public preview” mode from leaking into
   the normal logged-in app.
   - Your screenshot service sets sessionStorage.isPublicPreview.
   - useStorageUrl() treats that flag as “always use public GCS URLs”.
   - If it persists, HomeCard will try storage.googleapis.com URLs
     even for originals (and those may 403), causing fallback.
  ─────────────────────────────────────────────────────────────── */
useEffect(() => {
  try {
    if (typeof window === 'undefined') return;

    const isPublicPreviewRoute =
      window.location?.pathname?.startsWith('/public-preview');

    if (!isPublicPreviewRoute) {
      const raw = window.sessionStorage?.getItem('isPublicPreview');
      if (raw != null) {
        // remove both legacy "true" and wrapped { value:true, timestamp }
        window.sessionStorage.removeItem('isPublicPreview');
      }
    }
  } catch (_) {
    // non-fatal
  }
}, []);

/* ───────── Screenshot → permanent URL  ──────────────────────────
 * • 400-px thumbnail first (fast)
 * • Fallback to full-size original if thumb 404s/403s
 * • Final fallback = legacy signed URL (old schema)
 * ----------------------------------------------------------------*/
const thumbPath = form.screenshotThumbnail || null;   // 400-px WebP
const origPath  = form.screenshotPath      || null;   // full-size WebP

/* step-1: try the thumbnail
 *
 * ENTERPRISE:
 * HomeCard knows this Storage path is a form screenshot, so pass explicit
 * screenshot policy into useStorageUrl. This prevents Firebase Storage
 * getDownloadURL() 403s while preserving the hardened private-DAM behavior
 * for callers that do NOT know the asset is public-form-image.
 */
const { url: thumbUrl } = useStorageUrl(thumbPath, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'HomeCard.thumbnail',
});

/* step-2: ALSO resolve original eagerly.
 * Screenshots are public-eligible (vN_original.*), so this does not
 * weaken access control and prevents "thumb error => no fallback". */
const { url: origUrl } = useStorageUrl(origPath, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'HomeCard.original',
});

/* step-2.5: legacy fallback — some docs store screenshotUrl as a Storage PATH ("assets/...") */
const legacyPathMaybe =
  (typeof form?.screenshotUrl === 'string' && form.screenshotUrl.startsWith('assets/'))
    ? form.screenshotUrl
    : null;

const { url: legacyUrlFromPath } = useStorageUrl(legacyPathMaybe, {
  ...SCREENSHOT_STORAGE_URL_POLICY,
  debugLabel: 'HomeCard.legacyPath',
});

/* final pick order */
const screenshotUrlRaw =
        thumbUrl            // best (already https)
     || origUrl             // big but OK (already https)
     || legacyUrlFromPath   // if screenshotUrl was "assets/...", this becomes https
     || form.screenshotUrl  // may be https OR may be "assets/..." (legacy)
     || null;


/* Normalize: never allow a raw Storage path to hit <img src> */
const screenshotUrl =
  (typeof screenshotUrlRaw === 'string' && /^https?:\/\//i.test(screenshotUrlRaw))
    ? screenshotUrlRaw
    : null;


/* debug – comment out later */
//console.log('[HomeCard] screenshot debug:', {
//  formId,
//  thumbPath,
//  origPath,
//  thumbUrl,
//  origUrl,
//  legacy: form.screenshotUrl || null,
//  legacyPathMaybe,
//  legacyUrlFromPath,
//});


/* ───────────────────────────────────────────────────────────────
   Local <img> handling
   • imgSrc      – what the <img> is currently pointing at
   • tryOriginal – have we already attempted the full-size image?
   • Fallback    – when no usable screenshot exists, show a stable
                   per-form colour block instead of a broken-image icon
  ───────────────────────────────────────────────────────────────*/

// Treat "no screenshot yet" as a real fallback state (not "loading").
// This prevents a permanent skeleton when the screenshot fields aren’t present yet.
const hasScreenshot = Boolean(screenshotUrl);

const [imgSrc     , setImgSrc    ] = useState(hasScreenshot ? screenshotUrl : null);
const [imgLoaded  , setImgLoaded ] = useState(!hasScreenshot);
const [tryOriginal, setTryOriginal] = useState(false);

/* Enterprise: bounded retry so transient 404/ACL propagation doesn’t
 * require a full page refresh to recover. */
const MAX_IMG_RETRIES = 3;
const retryTimerRef = useRef(null);
const [imgRetryCount, setImgRetryCount] = useState(0);

/* whenever screenshotUrl changes → reset ¶ */
useEffect(() => {
  // Clear any scheduled retry when the source URL changes
  if (retryTimerRef.current) {
    try { clearTimeout(retryTimerRef.current); } catch (_) {}
    retryTimerRef.current = null;
  }

  const has = Boolean(screenshotUrl);
  setImgSrc(has ? screenshotUrl : null);
  setImgLoaded(!has);     // if no screenshot, we’re “done” (show fallback color)
  setTryOriginal(false);
  setImgRetryCount(0);
}, [screenshotUrl]);

// Unmount safety: prevent pending retry timer from firing after unmount.
useEffect(() => {
  return () => {
    if (retryTimerRef.current) {
      try { clearTimeout(retryTimerRef.current); } catch (_) {}
      retryTimerRef.current = null;
    }
  };
}, []);



/* ──────────────────────────────────────────────────────────────
   EFFECTIVE ROLE  ➜  per-form ACL overrides global role
   ─────────────────────────────────────────────────────────── */
const { hasPermission, currentUser, organizationId } = useAuth();

const effectiveRole = useMemo(() => {
  if (!currentUser) return 'none';

  /* 1️⃣  Form owner */
  if (form.owner === currentUser.uid) return 'owner';

  /* 2️⃣  Explicit ACL entry */
  if (form.sharedWith?.[currentUser.uid]) {
    return form.sharedWith[currentUser.uid];   // 'edit' | 'view'
  }

  /* 3️⃣  Org-wide access */
  if (form.organizationId === organizationId) {
    if (form.orgAccess === 'edit') return 'edit';
    if (form.orgAccess === 'view') return 'view';
  }

  return 'none';
}, [
  currentUser?.uid,
  form.owner,
  form.sharedWith,
  form.organizationId,
  form.orgAccess,
  organizationId,
]);

/* Derived capability flags */
const canEdit      = effectiveRole === 'edit' || effectiveRole === 'owner';
const canShare     = canEdit && hasPermission('shareForm');

/* ──────────────────────────────────────────────────────────────
   AUF marker – only for the special Asset Upload Form
   (we’ll use this to suppress Duplicate/Share just for AUF)
   ─────────────────────────────────────────────────────────── */
const isAUF =
  form?.isAssetUploadForm === true ||
  !!form?.assetIngestConfig ||
  form?.formType === 'assetUpload';

const isPublished = form.status === 'published';
const displayTitle =
  form.title ||
  form?.formDetails?.title ||
  'Untitled Form';

const displayDescription =
  form.description ||
  form?.formDetails?.description ||
  'No description available';


  // If the image fails and we fall back, we still want a visible color block.
  //
  // Enterprise:
  // - Keep the fallback color stable per form so cards do not visually
  //   flicker when parent state updates, screenshotStatus changes, or
  //   React re-renders the grid.
  const backgroundColor = useMemo(() => {
    const stableKey = String(formId || form?.title || 'fallback');
    let hash = 0;

    for (let i = 0; i < stableKey.length; i += 1) {
      hash = ((hash << 5) - hash) + stableKey.charCodeAt(i);
      hash |= 0; // Convert to 32-bit integer
    }

    return utmColors[Math.abs(hash) % utmColors.length];
  }, [formId, form?.title]);

  const handleShareOpen = useCallback((event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }

    setShowShareForm(true);
  }, []);

  const handleShareClose = useCallback((event) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }

    setShowShareForm(false);
  }, []);


  //Handle click on image or color block — navigate to published form if published
  const handlePreviewClick = useCallback(() => {
    if (isPublished) {
//      console.log(`[HomeCard] Navigating to published form: /forms/${formId}`);
      navigate(`/forms/${formId}`);
    } else {
//      console.log(`[HomeCard] Form ${formId} is not published. Click ignored.`);
    }
  }, [isPublished, navigate, formId]);

  return (
    <Grid
      item
      sx={{
        minWidth: HOME_FORM_CARD_WIDTH,
        width: HOME_FORM_CARD_WIDTH,
        position: 'relative',
        flex: '0 0 auto',
      }}
    >
      <Card
        elevation={0}
        sx={{
          mx: 0,
          my: 0,
          width: '100%',
          position: 'relative',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          cursor: isPublished ? 'pointer' : 'default',
        }}
      >
        {/* Share Icon Button positioned at the top right */}
        {canShare && !isAUF && (
          <IconButton
            aria-label={`Share form: ${displayTitle}`}
            onMouseDown={(event) => {
              // Prevent the preview ButtonBase from reacting before the
              // share icon receives the click.
              event.stopPropagation();
            }}
            onClick={handleShareOpen}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 4,
              p: '4px',
              backgroundColor: 'rgba(255,255,255,0.8)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
            }}
          >
            <ShareIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        )}

        {/* Button to view the form */}
        <ButtonBase
          disabled={!isPublished}
          aria-label={
            isPublished
              ? `Open published form: ${displayTitle}`
              : `Form is not published: ${displayTitle}`
          }
          sx={{
            width: '100%',
            height: HOME_FORM_CARD_IMAGE_HEIGHT,
            display: 'block',
            position: 'relative',
            border: '1px solid',
            borderColor: '#ccc',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
            cursor: isPublished ? 'pointer' : 'default',
            '&:hover': {
              borderColor: isPublished ? '#1976d2' : '#ccc',
            },
            '&.Mui-disabled': {
              opacity: 1,
              cursor: 'default',
            },
          }}
          onClick={handlePreviewClick} // ✅ NEW: Navigate to published form on click
        >
{/* ---- ACTUAL IMAGE --------------------------------------- */}
{imgSrc && (
  <img
    src={imgSrc}
    alt="Form thumbnail"
    loading="eager"
    decoding="async"
    onLoad={() => setImgLoaded(true)}
    onError={(e) => {
      const failedSrc = e?.currentTarget?.currentSrc || imgSrc;

      console.warn('[HomeCard] <img> failed:', {
        formId,
        failedSrc,
        thumbUrl,
        origUrl,
        tryOriginal,
      });

      /* 1st failure → try original; 2nd → give up */
      if (!tryOriginal && origUrl && failedSrc !== origUrl) {
        console.warn('[HomeCard] switching to original', origUrl);
        setImgLoaded(false);         // ensure skeleton shows while original loads
        setImgSrc(origUrl);
        setTryOriginal(true);
      } else {
        console.warn('[HomeCard] thumbnail & original failed – fallback');

        // Show color fallback immediately
        setImgSrc(null);
        setImgLoaded(true);

        // Bounded retry: helps when the form doc updates slightly before
        // the public object becomes readable / globally visible.
        if (imgRetryCount < MAX_IMG_RETRIES && screenshotUrl) {
          const nextCount = imgRetryCount + 1;
          setImgRetryCount(nextCount);

          const delayMs = 1500 * nextCount; // simple backoff: 1.5s, 3s, 4.5s

          if (retryTimerRef.current) {
            try { clearTimeout(retryTimerRef.current); } catch (_) {}
            retryTimerRef.current = null;
          }

          retryTimerRef.current = setTimeout(() => {
            // Re-attempt the best current URL.
            // Prefer thumb first, then original, then screenshotUrl.
            const retryUrl = thumbUrl || origUrl || screenshotUrl;

            if (retryUrl) {
              setImgLoaded(false);
              setImgSrc(retryUrl);
            }
          }, delayMs);
        }
      }
    }}
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: form.forceFit === 'contain' ? 'contain' : 'cover',
      objectPosition: 'top center',
      display: 'block',
      opacity: imgLoaded ? 1 : 0,
      transition: 'opacity 120ms ease',
      zIndex: 0,
    }}
  />
)}

{/* ---- lightweight grey skeleton while loading ------------- */}
{imgSrc && !imgLoaded && (
  <Box
    sx={{
      position : 'absolute',
      inset    : 0,
      bgcolor  : '#e0e0e0',
      animation: 'pulse 1.5s infinite ease-in-out',
      zIndex   : 1,
      pointerEvents: 'none',
    }}
  />
)}

{/* ---- coloured fallback block (no screenshot yet OR load failed) ---- */}
{!imgSrc && imgLoaded && (
  <Box
    sx={{
      position       : 'absolute',
      inset          : 0,
      zIndex         : 0,
      backgroundColor: backgroundColor,
    }}
  />
)}



          {/* Gray overlay across the entire preview area for drafts */}
          {!isPublished && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(80, 80, 80, 0.35)',
                zIndex: 1,
                pointerEvents: 'none', // ✅ Prevents blocking clicks
                borderBottom: '1px solid #D3D3D3', // ✅ Preserve border styling consistency
                borderRadius: 'inherit',
              }}
            />
          )}

          {/* ✅ Overlay 'DRAFT' label for non-published forms */}
          {!isPublished && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 600,
                px: 1,
                py: 0.25,
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                zIndex: 2,
                pointerEvents: 'none',
              }}
            >
              Draft
            </Box>
          )}

          {/* 🆕──────────────────────────────────────────────────────────
             TEMPLATE pill (shows only when this doc is a template)
             ─────────────────────────────────────────────────────────── */}
          {form.isTemplate && (
            <Box
              sx={{
                position       : 'absolute',
                bottom         : 8,
                left           : 8,
                backgroundColor: 'rgba(255,255,255,0.9)',
                color          : '#424242',
                fontSize       : '0.65rem',   // match description text size
                fontWeight     : 600,
                px             : 0.75,
                py             : 0.25,
                borderRadius   : '4px',
                textTransform  : 'uppercase',
                letterSpacing  : '0.5px',
                border         : '1px solid rgba(0,0,0,0.15)',
                zIndex         : 3,           // above image & draft overlay
                pointerEvents  : 'none',
              }}
            >
              Template
            </Box>
          )}
        </ButtonBase>

        <CardContent
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Form Title and MoreVert Icon in the same line */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {/* Form Title with Tooltip */}
            <Tooltip title={displayTitle} placement="top">
              <Typography
                gutterBottom
                variant="body2"
                sx={{
                  mt: 1,
                  mb: 0,
                  ml: 0.5,
                  lineHeight: 1,
                  textAlign: 'left',
                  fontSize: '0.80rem',
                  fontWeight: 600,
                  color: '#424242',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  flexGrow: 1, // ensures text shrinks so icon won't overlap
                  minWidth: 0,
                }}
              >
                {displayTitle}
              </Typography>
            </Tooltip>

            <HomeFormActionsMenu
              form={form}
              onDelete={onDelete}
              iconButtonSx={{
                p: 0.5,         // Reduce padding
                mr: 0,          // Remove margin-right on the icon
                mt: 1,
              }}
            />
          </Box>

          {/* Form Description with Tooltip */}
          <Tooltip title={displayDescription} placement="bottom">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.65rem',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box', // Limit to two lines with ellipsis
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal', // Prevents single-line truncation
                mt: 0.5,
                ml: 0.5,
                mb: 0,
                pr: 0.5,
              }}
            >
              {displayDescription}
            </Typography>
          </Tooltip>
        </CardContent>

        <Dialog
          open={showShareForm}
          onClose={handleShareClose}
          fullWidth
          maxWidth="sm"
          onClick={(event) => {
            // Keep dialog clicks from bubbling into the card.
            event.stopPropagation();
          }}
        >
          <Box sx={{ p: 3 }}>
          <ShareForm
            formName={displayTitle}
            formId={form.id}
            formUrl={isPublished ? `/forms/${form.id}` : null}
            formStatus={form.status}
            isPublic={form.isPublic}
            orgAccess={form.orgAccess}
            sharedWith={form.sharedWith}
            onClose={handleShareClose}
          />

          </Box>
        </Dialog>

      </Card>
    </Grid>
  );
}, areEqual);

// Helper function to determine if props are equal (optimization)
function areEqual(prevProps, nextProps) {
  return (
    prevProps.form.id                  === nextProps.form.id &&
    prevProps.form.title               === nextProps.form.title &&
    prevProps.form.description         === nextProps.form.description &&
    prevProps.form.status              === nextProps.form.status &&

    // Screenshot fields:
    // - screenshotThumbnail/screenshotPath are the modern Storage paths.
    // - screenshotUrl supports legacy resolver and old signed/public URL flows.
    // - screenshotStatus keeps queued/processing/ready transitions responsive.
    prevProps.form.screenshotPath      === nextProps.form.screenshotPath &&
    prevProps.form.screenshotThumbnail === nextProps.form.screenshotThumbnail &&

    // IMPORTANT: some flows only update screenshotUrl first (legacy / resolver)
    // If we don’t include this, the card can stay stale until a full refresh.
    prevProps.form.screenshotUrl       === nextProps.form.screenshotUrl &&

    // Optional but useful: keeps UI responsive to queued/processing/ready transitions
    prevProps.form.screenshotStatus    === nextProps.form.screenshotStatus &&

    // Visual rendering fields
    prevProps.form.forceFit            === nextProps.form.forceFit &&
    prevProps.form.isTemplate          === nextProps.form.isTemplate &&

    // Permission / action visibility fields.
    // Keep these in HomeCard's memo comparison because HomeCard:
    // - renders the direct Share shortcut
    // - passes the full form into HomeFormActionsMenu
    // If these fields change and this component skips rendering, the
    // share shortcut and shared action menu would not receive updated
    // permission/action props.
    prevProps.form.owner               === nextProps.form.owner &&
    prevProps.form.createdBy           === nextProps.form.createdBy &&
    prevProps.form.organizationId      === nextProps.form.organizationId &&
    prevProps.form.orgAccess           === nextProps.form.orgAccess &&
    prevProps.form.sharedWith          === nextProps.form.sharedWith &&
    prevProps.form.isPublic            === nextProps.form.isPublic &&

    // AUF suppression fields
    prevProps.form.isAssetUploadForm   === nextProps.form.isAssetUploadForm &&
    prevProps.form.assetIngestConfig   === nextProps.form.assetIngestConfig &&
    prevProps.form.formType            === nextProps.form.formType &&

    prevProps.onDelete                 === nextProps.onDelete
  );
}


export default HomeCard;
